import { useIsFullscreen } from "@/hooks/useIsFullscreen";
import { useAuth } from "@canucci/base/auth";
import { dialogueEmail } from "@canucci/base/env";
import { DecorativeCircles } from "@canucci/base-mantine/components";
import {
  darkSurfaceDecorationCircles,
  darkSurfaceCssVars,
  darkSurfaceScopeClass,
  darkSurfaceStyle,
  dockedBarStyle,
  dockedTopBarStyle,
} from "@canucci/base-mantine/theme";
import { Box, Flex, Anchor, Text, useMantineTheme } from "@mantine/core";
import React from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";
import { BackLinkConfig } from "./CanvasLayoutContext";
import { CanvasHeader } from "../components/layout/CanvasHeader";

interface StandardLayoutProps {
  background?: string;
  parentPath?: string;
  backLink?: BackLinkConfig;
  children?: React.ReactNode;
  showMenuButton?: boolean;
  hasBackButton?: boolean;
  darkBackground?: boolean;
  title?: string;
  subtitle?: string;
  availableLanguages?: string[];
  fullWidth?: boolean;
  footerContent?: React.ReactNode;
  showHeader?: boolean;
  showDecorations?: boolean;
  showFullscreen?: boolean;
}

function StandardLayout({
  background,
  darkBackground,
  parentPath,
  backLink,
  hasBackButton,
  children,
  title,
  subtitle,
  availableLanguages,
  fullWidth,
  footerContent,
  showHeader = true,
  showDecorations = true,
  showFullscreen = true,
}: StandardLayoutProps) {
  const { user } = useAuth();
  const theme = useMantineTheme();
  const { sizes } = theme.other;
  const { t: tCommon } = useTranslation("common");
  // Fullscreen is a presentation mode: the canvas content gets the whole screen, so the chrome steps aside.
  const isFullscreen = useIsFullscreen();
  const showChrome = !isFullscreen;

  let backAction = undefined;

  if (hasBackButton && parentPath) {
    backAction = {
      to: parentPath,
      label: undefined,
    };
  } else if (!hasBackButton && backLink) {
    backAction = {
      to: backLink.to,
      label: backLink.label,
    };
  }

  return (
    <Flex
      maw="100%"
      w="100%"
      direction="column"
      h="100vh"
      pos="relative"
      style={{
        ...(darkBackground ? darkSurfaceCssVars : {}),
        overflowX: "hidden",
        overflowY: "auto",
      }}
    >
      {showHeader && showChrome && (
        <Box
          style={{
            ...(darkBackground ? dockedTopBarStyle : {}),
            position: "relative",
            zIndex: 1,
            flexShrink: 0,
          }}
        >
          <CanvasHeader
            title={title}
            subtitle={subtitle}
            backAction={backAction}
            darkBackground={darkBackground}
            availableLanguages={availableLanguages}
            user={user}
            hasBackButton={hasBackButton || !!backAction}
            showFullscreen={showFullscreen}
          />
        </Box>
      )}
      <Box
        component="main"
        className={darkBackground ? darkSurfaceScopeClass : undefined}
        flex={1}
        w="100%"
        maw={fullWidth ? "100%" : sizes.maxPageWidthDesktop}
        pos="relative"
        px={isFullscreen ? "xl" : undefined}
        py={isFullscreen ? "2xl" : undefined}
        style={{
          ...(darkBackground ? darkSurfaceStyle : {}),
          alignSelf: "center",
          overflowX: "hidden",
          ...(isFullscreen ? { display: "flex", flexDirection: "column" } : {}),
        }}
        bg={background}
      >
        {darkBackground && showDecorations ? <DecorativeCircles circles={darkSurfaceDecorationCircles} /> : null}
        <Box
          pos="relative"
          style={{
            zIndex: 1,
            // Auto margins centre the content while there is room and collapse to 0 once it overflows.
            // justify-content: center would instead push the overflowing top edge out of scroll reach,
            // because overflow-x: hidden above makes this a scroll container.
            ...(isFullscreen ? { marginBlock: "auto" } : {}),
          }}
        >
          {children || <Outlet />}
        </Box>
      </Box>
      {showChrome && (
        <Box
          component="footer"
          flex={0}
          w="100%"
          c={darkBackground ? "var(--text-on-dark-muted)" : undefined}
          style={{
            ...(darkBackground ? dockedBarStyle : {}),
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box w="100%" ta="center" pt="md" pb="md">
            {footerContent ?? (
              <Text>
                {tCommon("footer.text_start")}{" "}
                <Box component="span" style={{ whiteSpace: "nowrap" }}>
                  {tCommon("footer.text_cta")}{" "}
                  <Anchor
                    href={`mailto:${dialogueEmail()}`}
                    target="_blank"
                    c={darkBackground ? "var(--text-on-dark)" : undefined}
                    style={{
                      outline: "none",
                      textDecoration: "underline",
                      display: "inline-block",
                    }}
                  >
                    {dialogueEmail()}
                  </Anchor>
                </Box>
              </Text>
            )}
          </Box>
        </Box>
      )}
    </Flex>
  );
}

export default StandardLayout;
